/**
 * Settings-screen state — `backend/src/game/account/` plus two core routes.
 *
 * ─── Three servers, one store ───────────────────────────────────────────────
 * The Settings screen is the one surface that talks to more than its own
 * module, and the split is not arbitrary:
 *
 *   `GET|PATCH /account`        the game's own account module (name, billing)
 *   `POST /account/password`    same module; revokes every session
 *   `DELETE /account`           same module; refused for admins
 *   `GET /account/ledger`       same module; Cash/Gold history with labels
 *   `GET /account/invoices`     same module; core's list plus `paymentMethod`
 *   `GET|PATCH /me/preferences` CORE owns locale + currency, and validates
 *                               each against its own enabled list
 *   `GET /i18n/config`          CORE's enabled locales and currencies
 *   `GET /cms/legal/:slug`      CORE's CMS; rows seeded by this game
 *
 * Locale and currency are READ off `GET /account` and WRITTEN to core's
 * preferences route. That asymmetry is the server's, not an oversight here —
 * `account.dto.ts` rejects both fields by name so that a client sending them
 * to the wrong endpoint gets a 400 pointing at the right one.
 *
 * ─── What this store does NOT hold ──────────────────────────────────────────
 * Sound settings. They never reach a server (there is no audio engine yet at
 * all) and live in `audio-settings.ts` against localStorage.
 */
import { createStore } from '../../core/state/store';
import { api, API_BASE, ApiError } from '../../core/network/api';
import type {
  AccountInvoicePage, AccountLedgerPage, AccountProfile, AccountProfilePatch,
  AccountState, I18nConfig, ProfileField, SettingsTab,
} from './account-state.types';

/** The three documents the Legal tab links to (`prisma/content/legal-pages.ts`); see `landing/legal-paths.ts` for their URLs. */
export const LEGAL_DOCUMENTS: { slug: string; label: string }[] = [
  { slug: 'privacy-policy', label: 'Privacy Policy' },
  { slug: 'terms-of-service', label: 'Terms of Service' },
  { slug: 'cookie-policy', label: 'Cookie Policy' },
];

/** Roles that may open the admin panel. Mirrors the `Role` enum's non-player members. */
const ADMIN_ROLES = new Set(['ADMIN', 'KYC_ADMIN']);

const INITIAL_STATE: AccountState = {
  profile: null,
  i18n: null,
  ledger: null,
  invoices: null,
  tab: 'PROFILE',
  busy: false,
  error: null,
  notice: null,
  sessionEnded: false,
  billingErrors: {},
};

export const accountStateStore = createStore<AccountState>({ ...INITIAL_STATE });

// ─── Reads ──────────────────────────────────────────────────────────────────

/**
 * Loads what the screen needs to paint at all: the profile and the enabled
 * locale/currency lists.
 *
 * The other three tabs fetch lazily on first open. Invoices in particular is
 * behind `PreviewDisabledGuard` on the server and 404s in a preview build, so
 * pulling it up front would put an error banner over a Settings screen whose
 * other four tabs are perfectly fine.
 *
 * `/i18n/config` is fetched tolerantly: it is a dropdown's worth of options,
 * and a player who cannot reach it should still be able to fix their billing
 * address.
 */
export async function loadAccount(): Promise<void> {
  accountStateStore.set((s) => ({ ...s, busy: true, error: null }));
  try {
    const [profile, i18n] = await Promise.all([
      api.get<AccountProfile>('/account'),
      api.get<I18nConfig>('/i18n/config').catch(() => null),
    ]);
    accountStateStore.set((s) => ({ ...s, profile, i18n, busy: false }));
  } catch (err) {
    accountStateStore.set((s) => ({
      ...s, busy: false, error: msg(err, 'Couldn’t load your account.'),
    }));
  }
}

export async function loadLedger(): Promise<void> {
  accountStateStore.set((s) => ({ ...s, busy: true, error: null }));
  try {
    const ledger = await api.get<AccountLedgerPage>('/account/ledger?limit=50');
    accountStateStore.set((s) => ({ ...s, ledger, busy: false }));
  } catch (err) {
    accountStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn’t load your ledger.') }));
  }
}

export async function loadInvoices(): Promise<void> {
  accountStateStore.set((s) => ({ ...s, busy: true, error: null }));
  try {
    const invoices = await api.get<AccountInvoicePage>('/account/invoices');
    accountStateStore.set((s) => ({ ...s, invoices, busy: false }));
  } catch (err) {
    // A preview build answers 404 here by design (invoices are payment
    // artefacts). Reported as an empty page rather than an error, so the tab
    // reads "nothing billed yet" instead of "something is broken".
    accountStateStore.set((s) => ({
      ...s,
      busy: false,
      invoices: { items: [], page: 1, pageSize: 20, total: 0 },
      error: null,
    }));
    void err;
  }
}

// ─── Writes ─────────────────────────────────────────────────────────────────

/**
 * Saves the name and billing address.
 *
 * Sends only the keys it was given. `PATCH /account` leaves an omitted key
 * alone and CLEARS one sent as `""`, so a form that posted every field on
 * every save would wipe whatever the player had not touched in this session
 * if the panel's copy were ever stale.
 */
export async function saveProfile(patch: AccountProfilePatch): Promise<void> {
  accountStateStore.set((s) => ({
    ...s, busy: true, error: null, notice: null, billingErrors: {},
  }));
  try {
    const profile = await api.patch<AccountProfile>('/account', patch);
    accountStateStore.set((s) => ({
      ...s, profile, busy: false, notice: 'Saved.',
    }));
  } catch (err) {
    accountStateStore.set((s) => ({
      ...s,
      busy: false,
      error: msg(err, 'Couldn’t save that.'),
      billingErrors: billingErrorsOf(err),
    }));
  }
}

/** Core billing field → the `PATCH /account` key the form uses for it. */
export const CORE_TO_PROFILE_FIELD: Record<string, ProfileField> = {
  firstName: 'firstName',
  lastName: 'lastName',
  line1: 'billingLine1',
  line2: 'billingLine2',
  city: 'billingCity',
  zip: 'billingZip',
  state: 'billingState',
  country: 'billingCountry',
};

/**
 * `INVALID_BILLING` carries `details: { <core field>: message }` — the server's
 * copy of the same rules the form checks inline, so this only fires when the
 * two disagree (or the stored address changed under the form).
 */
function billingErrorsOf(err: unknown): Partial<Record<ProfileField, string>> {
  if (!(err instanceof ApiError) || err.code !== 'INVALID_BILLING') return {};
  const details = (err.details as { details?: Record<string, string> } | undefined)?.details ?? {};
  const out: Partial<Record<ProfileField, string>> = {};
  for (const [field, message] of Object.entries(details)) {
    const key = CORE_TO_PROFILE_FIELD[field];
    if (key) out[key] = message;
  }
  return out;
}

/** Inline client-side errors, set before a save is even attempted. */
export function setBillingErrors(errors: Partial<Record<ProfileField, string>>): void {
  accountStateStore.set((s) => ({
    ...s, billingErrors: errors, notice: null,
    error: Object.keys(errors).length ? 'Check the highlighted fields.' : null,
  }));
}

/**
 * Changes the language and/or currency through CORE's preferences route.
 *
 * Deliberately a separate call from `saveProfile`: core validates a locale
 * against its enabled set and a currency against `CurrencyService`, and the
 * game's own PATCH rejects both fields by name. Re-reads the profile
 * afterwards so the form reflects what the server stored (it uppercases the
 * currency), rather than what was typed.
 */
export async function savePreferences(
  prefs: { locale?: string; currency?: string },
): Promise<void> {
  if (prefs.locale === undefined && prefs.currency === undefined) return;
  accountStateStore.set((s) => ({
    ...s, busy: true, error: null, notice: null,
  }));
  try {
    await api.patch('/me/preferences', prefs);
    const profile = await api.get<AccountProfile>('/account');
    accountStateStore.set((s) => ({
      ...s, profile, busy: false, notice: 'Saved.',
    }));
  } catch (err) {
    accountStateStore.set((s) => ({
      ...s, busy: false, error: msg(err, 'Couldn’t change that setting.'),
    }));
  }
}

/**
 * Changes the password.
 *
 * On success EVERY refresh token is revoked, this session's included, so the
 * only honest thing to do is say so and send the player to the login screen.
 * `sessionEnded` is what the screen renders; the actual sign-out is the
 * caller's, because logging out is `bootstrap.ts`'s job and this module has no
 * business reaching into it.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<boolean> {
  accountStateStore.set((s) => ({
    ...s, busy: true, error: null, notice: null,
  }));
  try {
    await api.post('/account/password', { currentPassword, newPassword });
    accountStateStore.set((s) => ({
      ...s, busy: false, sessionEnded: true, notice: 'Password changed. Every session was signed out.',
    }));
    return true;
  } catch (err) {
    accountStateStore.set((s) => ({
      ...s, busy: false, error: msg(err, 'Couldn’t change your password.'),
    }));
    return false;
  }
}

/**
 * Deletes the account, permanently.
 *
 * Returns whether it went through, so the caller signs out only on success —
 * an ADMIN account is refused server-side and must stay signed in.
 *
 * ─── Why this one call does not go through `api` ────────────────────────────
 * The password travels in a BODY. `DeleteAccountDto`'s own comment explains
 * why: Nest does read a body on DELETE, and a query string would land in
 * access logs, proxy logs and browser history. But `api.del(path, opts)`
 * (`core/network/api.ts`) takes no body at all — `RequestOptions` carries only
 * headers, a signal and a retry flag — and `core/` is not editable from a game
 * repo, so widening it is not on the table here.
 *
 * `fetch` is therefore correct rather than lazy, and it is safe for the same
 * reason the rest of this client works: auth on this stack is a COOKIE, not an
 * Authorization header (there is no request interceptor in `api.ts` — only a
 * response one), so `credentials: 'include'` sends exactly what axios sends.
 *
 * What it does NOT inherit is the 401-refresh interceptor, and that gap is
 * real: a 15-minute access token can expire while the Settings screen sits
 * open, and a delete that failed with no way to recover is the worst button on
 * the screen to get this wrong on. So a 401 is handled the way the interceptor
 * would — one read through `api` (which refreshes and retries on the way), one
 * retry — rather than reported to the player as "couldn't delete".
 */
export async function deleteAccount(password: string): Promise<boolean> {
  accountStateStore.set((s) => ({
    ...s, busy: true, error: null, notice: null,
  }));
  try {
    let res = await deleteRequest(password);
    if (res.status === 401) {
      // Refreshes the session as a side effect — `api`'s response interceptor
      // catches the 401, posts /auth/refresh and replays. If that fails too,
      // it throws and the catch below reports an honest "not signed in".
      await api.get<AccountProfile>('/account');
      res = await deleteRequest(password);
    }
    if (!res.ok) throw new Error(await readError(res));
    accountStateStore.set((s) => ({ ...s, busy: false, sessionEnded: true }));
    return true;
  } catch (err) {
    accountStateStore.set((s) => ({
      ...s, busy: false, error: msg(err, 'Couldn’t delete the account.'),
    }));
    return false;
  }
}

function deleteRequest(password: string): Promise<Response> {
  return fetch(`${API_BASE}/account`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
}

/**
 * The server's own message, if it sent one.
 *
 * Nest's exception filter answers `{ statusCode, error, message }`, and
 * `message` is the sentence worth showing — "Password is incorrect" is a
 * different problem from "Admin accounts cannot be deleted here", and a
 * generic failure line would make the two indistinguishable.
 */
async function readError(res: Response): Promise<string> {
  try {
    const body = await res.json() as { message?: string | string[] };
    const m = Array.isArray(body.message) ? body.message[0] : body.message;
    if (m) return m;
  } catch {
    // Not JSON — a proxy error page, say. Fall through to the status.
  }
  return `Delete failed (${res.status}).`;
}

// ─── Plumbing ───────────────────────────────────────────────────────────────

export function setSettingsTab(tab: SettingsTab): void {
  accountStateStore.set((s) => ({
    ...s, tab, error: null, notice: null,
  }));
}

export function clearAccountMessages(): void {
  accountStateStore.set((s) => ({ ...s, error: null, notice: null, billingErrors: {} }));
}

export function resetAccountState(): void {
  accountStateStore.set({ ...INITIAL_STATE });
}

/**
 * May this account open the admin panel?
 *
 * Read off the SERVER's `role`, never off anything the client decides. This
 * only hides a link — every endpoint behind the panel is guarded again on the
 * server by `RolesGuard` + `@Roles('ADMIN')` — so getting it wrong is a
 * cosmetic bug and not a hole, which is exactly why it must not be mistaken
 * for the access control.
 */
export function canOpenAdmin(profile: { role: string } | null): boolean {
  return !!profile && ADMIN_ROLES.has(profile.role);
}

function msg(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}
